/**
 * Centralized constants for the Vale Dashboard application
 *
 * This barrel file exports all application constants from a single location.
 * Import constants like:
 *
 * @example
 * import { MAX_FILE_SIZE, SEARCH_DEBOUNCE_MS } from '@/lib/constants';
 * import { MATTER_KEYS } from '@/lib/constants';
 * import { MATTER_ROUTES } from '@/lib/constants';
 */

// File limits and validation
export * from './file-limits';

// Pagination defaults
export * from './pagination';

// Timeouts and durations
export * from './timeouts';

// React Query cache keys
export * from './query-keys';

// Application routes
export * from './routes';

// Help Centre deep-link mapping
export * from './help-centre';

// Which artefacts count as a letterhead, and the flag that offers the .docx one
export * from './letterhead-artefacts';

// Australian-specific constants (states, countries, roles, titles)
export * from './australian-constants';

// Property title-reference terminology, formatting and validation (per state)
export * from './title-reference';

// Matter type badge colors
export * from './matter-type-colors';

export * from './intake-sections';

// Relationship-to-deceased options
export * from './relationships';

// Canonical inventory-type display order (assets / liabilities)
export * from './inventory-type-order';

// Fixed app-wide chrome other anchored surfaces have to clear
export * from './layout';

// Re-export commonly used constants for convenience
export {
  MAX_FILE_SIZE,
  MAX_SIGNATURE_SIZE,
  MAX_FILE_SIZE_MB
} from './file-limits';
export {
  FILE_KEYS,
  DOCUMENT_KEYS,
  MATTER_KEYS,
  TASK_KEYS,
  EXPORT_KEYS,
  CORRESPONDENCE_EXPORT_KEYS
} from './query-keys';
export {
  PREVIEW_CONTACTS_COUNT,
  PREVIEW_INVENTORY_COUNT,
  PREVIEW_TIMELINE_COUNT,
  SEARCH_RESULTS_LIMIT
} from './pagination';
export {
  SEARCH_DEBOUNCE_MS,
  EXPORT_POLLING_INTERVAL_MS,
  EXPORT_POLLING_TIMEOUT_MS,
  AI_ANALYSIS_POLLING_INTERVAL_MS,
  TITLE_SEARCH_OPTIMISTIC_WINDOW_MS,
  TITLE_SEARCH_INFLIGHT_POLL_INTERVAL_MS,
  PMS_CONTACT_BINDING_INFLIGHT_POLL_INTERVAL_MS,
  PMS_CONTACT_BINDING_MAX_INFLIGHT_POLLS
} from './timeouts';
export {
  AUSTRALIAN_STATES,
  LEGAL_PERSONAL_REPRESENTATIVE_ROLES
} from './australian-constants';
export { COUNTRY_NAMES, COUNTRY_OPTIONS, DEFAULT_COUNTRY } from './countries';
export type { CountryName } from './countries';
export {
  EMAIL_BODY_FONT_STACKS,
  EMAIL_BODY_FONT_FAMILIES,
  EMAIL_BODY_FONT_SIZES_PT,
  DEFAULT_EMAIL_BODY_FONT_FAMILY,
  DEFAULT_EMAIL_BODY_FONT_SIZE_PT,
  EMAIL_BODY_MAX_WIDTH_PX
} from './email-typography';
export type {
  EmailBodyFontFamily,
  EmailBodyFontSizePt
} from './email-typography';
